'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';
import { Button } from '../button';
const POPOVER_MAX_WIDTH = 640;
/**
 * The list-page filter look for pages that are not a DataTable (reports):
 * a popover under the Filters button with a 2-column field grid and
 * Clear All · Cancel · Apply in the footer. Place fields with <FilterField>.
 */
export function FilterPopover({ isOpen, onClose, anchorRef, title = 'Filters', appliedCount = 0, onApply, onClear, clearDisabled, applyLabel = 'Apply', applyDisabled, footerExtra, maxWidth = POPOVER_MAX_WIDTH, columns = 2, children, }) {
    const [position, setPosition] = useState(null);
    useLayoutEffect(() => {
        if (!isOpen)
            return;
        const place = () => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (!rect)
                return;
            const top = rect.bottom + 8;
            const width = Math.min(maxWidth, window.innerWidth - 32);
            // Right-align to the button when there is room (toolbar buttons on the right),
            // otherwise left-align (Filters button on the left of a report toolbar)
            const rightAligned = rect.right - width;
            const left = rightAligned >= 16
                ? rightAligned
                : Math.max(16, Math.min(rect.left, window.innerWidth - width - 16));
            setPosition({
                top,
                left,
                width,
                maxHeight: Math.max(240, window.innerHeight - top - 16),
            });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [isOpen, anchorRef, maxWidth]);
    useEffect(() => {
        if (!isOpen)
            return;
        const onKeyDown = (e) => {
            // react-select handles (and preventDefaults) Escape while its menu is open
            if (e.key === 'Escape' && !e.defaultPrevented)
                onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);
    if (!isOpen || !position || typeof document === 'undefined')
        return null;
    return createPortal(_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 z-40", onClick: onClose }), _jsxs("div", { role: "dialog", "aria-label": title, className: "fixed z-50 flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl", style: { top: position.top, left: position.left, width: position.width, maxHeight: position.maxHeight }, children: [_jsxs("div", { className: "flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700", children: [_jsxs("div", { className: "flex items-baseline gap-2", children: [_jsx("h2", { className: "text-sm font-semibold text-gray-900 dark:text-gray-100", children: title }), appliedCount > 0 && (_jsxs("span", { className: "text-xs text-gray-500 dark:text-gray-400", children: [appliedCount, " applied"] }))] }), _jsx("button", { type: "button", onClick: onClose, className: "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors", "aria-label": "Close filters", children: _jsx("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) })] }), _jsx("div", { className: "flex-1 min-h-0 overflow-y-auto px-4 py-3", children: _jsx("div", { className: cn('grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3', columns === 3 && 'lg:grid-cols-3'), children: children }) }), _jsxs("div", { className: "flex items-center gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700", children: [onClear && (_jsx(Button, { variant: "outline", size: "sm", onClick: onClear, disabled: clearDisabled, children: "Clear All" })), footerExtra, _jsx("div", { className: "flex-1" }), _jsx(Button, { variant: "outline", size: "sm", onClick: onClose, children: "Cancel" }), _jsx(Button, { size: "sm", onClick: onApply, disabled: applyDisabled, children: applyLabel })] })] })] }), document.body);
}
/** One labelled field inside <FilterPopover> — same label style as the list-page filters */
export function FilterField({ label, labelExtra, full, className, children }) {
    return (_jsxs("div", { className: cn('space-y-1 min-w-0', full && 'sm:col-span-full', className), children: [_jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsx("label", { className: "text-xs font-medium text-gray-600 dark:text-gray-300", children: label }), labelExtra] }), children] }));
}
